import {
    DrivewealthAccountManagementType,
    FetchState,
    UserHsaAccountStatus
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as BankService from "@/shared/api/bankService";
import * as DrivewealthService from "@/shared/api/drivewealthService";

import {
    InvestmentAccountRepointState,
    HsaAccountDetails,
    InvestmentAccountRepointRequest,
    InvestmentAccountDetails,
    InvestmentRepointRequest,
    InvestmentRepointRequestResult
} from "@/admin/types";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { defineStore } from "pinia";

export const initialState: InvestmentAccountRepointState = {
    getRequestFetchState: FetchState.Uninitialized,
    postRequestFetchState: FetchState.Uninitialized,
    hsaAccounts: [],
    investmentRepointRequests: [],
    investmentRepointRequestPages: [],
    pageNumber: 1,
    pageCount: 0,
};


export const useInvestmentAccountRepointStore = defineStore("investmentAccountRepoint", {
    state: (): InvestmentAccountRepointState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: (state): boolean => state.getRequestFetchState === FetchState.Loaded,
        isLoading: (state): boolean => state.getRequestFetchState === FetchState.Loading,
        isPosting: (state): boolean => state.postRequestFetchState === FetchState.Loading,
        hsaAccountWithInvestments: (state): HsaAccountDetails => _.find(state.hsaAccounts, { hasInvestments: true }),
        getInvestmentRepointRequestsByPageNumber: state => (pageNumber: number): InvestmentRepointRequest[] => {
            const pageObject = state.investmentRepointRequestPages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
    },
    actions: {
        setGetRequestFetchState (fetchState: FetchState) {
            this.getRequestFetchState = fetchState;
        },
        setPostRequestFetchState (fetchState: FetchState) {
            this.postRequestFetchState = fetchState;
        },
        setHsaAccounts (hsaAccounts: HsaAccountDetails[]) {
            this.hsaAccounts = [ ...hsaAccounts ];
        },
        setInvestmentRepointRequests (repointRequest: InvestmentRepointRequest[]) {
            this.investmentRepointRequests = [ ...repointRequest ];
        },
        setInvestmentRepointRequestPages (investmentRepointRequestResult: InvestmentRepointRequestResult) {
            const investmentRepointRequestPages = [ ...this.investmentRepointRequestPages ];
            investmentRepointRequestPages[investmentRepointRequestResult.pageNumber] = investmentRepointRequestResult.investmentRepointRequests;

            this.pageCount = investmentRepointRequestResult.pageCount;
            this.pageNumber = investmentRepointRequestResult.pageNumber;
            this.investmentRepointRequestPages = investmentRepointRequestPages;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        resetState () {
            this.getRequestFetchState = FetchState.Uninitialized;
            this.hsaAccounts = [];
            this.investmentAccounts = [];
        },
        getHsaAccounts (ssn: string) {
            let accountDetails: HsaAccountDetails[];
            return new Promise<void>((resolve, reject) => {
                if (this.getRequestFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    const formattedSsn = ssn.replace(/-/g, "");
                    this.setGetRequestFetchState(FetchState.Loading);
                    return BankService.getUserHsaAccountsBySsn(formattedSsn)
                    .then(resp => {
                        accountDetails = resp.data;
                        return accountDetails;
                    })
                    .then(result => {
                        accountDetails.forEach(r => {
                            r.investmentFetchState = FetchState.Loading;
                            DrivewealthService.getUserInvestmentAccountsByUsername(r.cognitoName).then(p => {
                                r.investmentAccounts = p.data;
                                r.hasInvestments = p.data.length > 0;
                                r.investmentFetchState = FetchState.Loaded;
                                return r;
                            }).catch(err => {
                                storeHelper.handleError({ error: err, rethrow: true });
                                r.investmentAccounts = [];
                                r.investmentFetchState = FetchState.Loaded;
                            });
                        });
                    }).then(r => {
                        this.setHsaAccounts(accountDetails);
                        this.setGetRequestFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                        this.setGetRequestFetchState(FetchState.Loaded);
                    });
                }
            });
        },
        saveRepointingRequest (request: InvestmentAccountRepointRequest) {
            return new Promise<void>((resolve, reject) => {
                if (this.postRequestFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setPostRequestFetchState(FetchState.Loading);
                    return BankService.saveRepointInvestmentAccount(request).then(resp => {
                        this.setPostRequestFetchState(FetchState.Loaded);
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess(`The request has been submitted succesfully to repoint investment accounts.`);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        getInvestmentRepointRequests (pageNumber: number) {
            this.setGetRequestFetchState(FetchState.Loading);
            const pageSizeStore = usePageSizeStore();
            return BankService.getInvestmentRepointRequests(pageNumber, pageSizeStore.pageSize)
            .then(response => {
                this.setInvestmentRepointRequestPages(response.data);
                this.setGetRequestFetchState(FetchState.Loaded);
            }).catch(err => {
                this.setGetRequestFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
    }
});